import { useDispatch, useSelector } from 'react-redux';
import * as luxon from 'luxon';
import {
  formatDay,
  formatWeek,
  checkItIsToday,
  formatMonth,
  formatYear,
  isSevenDaysApart,
  getStartDate,
  getEffectiveEndDate,
} from './helpers';

import { CALENDAR } from '@flexo/reducers';
import { IconWrapper } from '@flexo/general';
import { useTranslation } from 'react-i18next';

import styles from './hive-calendar.module.scss';

const ENV = (import.meta as any).env;

export function HiveCalendarInput(props: any) {
  const { showCalendar, setShowCalendar, calendarColor } = props;
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const calendar = useSelector((state: any) => state.calendar);
  const appConfigUpperBound = useSelector(
    (state: any) => state?.appConfig?.calendar?.upperBound || null
  );
  const hiveCalendar = calendar.hiveCalendar;
  const { selectedTimeSet, selectedDate } = hiveCalendar;

  const startDate = getStartDate();

  function getCalendarInputText() {
    const timeSet = selectedTimeSet;
    const _selectedDate = selectedDate;

    switch (timeSet) {
      case 'day':
        if (checkItIsToday(_selectedDate)) {
          return t('widgets.calendar.today');
        } else {
          return formatDay(_selectedDate);
        }
      case 'week':
        if (isSevenDaysApart(_selectedDate[0], _selectedDate[1])) {
          return formatWeek(_selectedDate);
        } else {
          if (checkItIsToday(_selectedDate)) {
            return t('widgets.calendar.today');
          } else {
            return formatDay(_selectedDate);
          }
        }
      case 'month':
        return formatMonth(_selectedDate as any /* [Date,Date] */, i18n.language as any);

      case 'year':
        return formatYear(_selectedDate);

      default:
        return _selectedDate[0]?.toDateString();
    }
  }

  function getFurtherOptions() {
    let options: any = { days: 1 };
    switch (selectedTimeSet) {
      case 'day':
        options = { days: 1 };
        break;
      case 'week':
        options = { days: 7 };
        break;
      case 'month':
        options = { months: 1 };
        break;
      case 'year':
        options = { years: 1 };
        break;
      default:
        break;
    }

    const _nextFirstDate = luxon.DateTime.fromJSDate(selectedDate[0])
      .plus(options)
      .startOf('day');
    const _nextSecondDate = luxon.DateTime.fromJSDate(selectedDate[1])
      .plus(options)
      .startOf('day');
    // Use effectiveEndDate (min between entity endDate and configured upper bound) as upper limit
    const _upperLimit = luxon.DateTime.fromJSDate(getEffectiveEndDate(appConfigUpperBound)).startOf('day');

    return { options, _nextFirstDate, _nextSecondDate, _upperLimit };
  }

  function canGoFurther() {
    const { _nextFirstDate, _upperLimit } = getFurtherOptions();

    if (selectedTimeSet === 'year') {
      return _nextFirstDate.year <= _upperLimit.year;
    }

    if (_nextFirstDate > _upperLimit) {
      return false;
    } else {
      return true;
    }
  }

  function getPreviuosOptions() {
    let options: any = { days: 1 };
    switch (selectedTimeSet) {
      case 'day':
        options = { days: 1 };
        break;
      case 'week':
        options = { days: 7 };
        break;
      case 'month':
        options = { months: 1 };
        break;
      case 'year':
        options = { years: 1 };
        break;
      default:
        break;
    }

    const _previousFirstDate = luxon.DateTime.fromJSDate(selectedDate[0])
      .minus(options)
      .startOf('day');
    const _previousSecondDate = luxon.DateTime.fromJSDate(selectedDate[1])
      .minus(options)
      .startOf('day');
    const _today = luxon.DateTime.fromJSDate(new Date()).startOf('day');

    return { options, _previousFirstDate, _previousSecondDate, _today };
  }

  function canGoBackward() {
    if (!startDate) {
      return true;
    }

    const { _previousFirstDate } = getPreviuosOptions();

    const luxonStartDate = luxon.DateTime.fromJSDate(
      new Date(startDate)
    ).startOf('day');



    if (selectedTimeSet === 'year') {
      return  _previousFirstDate.year >= luxonStartDate.year;
    }

    if (selectedTimeSet === 'month') {
      if (_previousFirstDate.year === luxonStartDate.year) {
        return _previousFirstDate.month >= luxonStartDate.month;
      } else {
        return _previousFirstDate.year >= luxonStartDate.year;
      }

      
    }

    if (_previousFirstDate < luxonStartDate) {
      return false;
    } else {
      return true;
    }
  }

  function goToNextDate() {
    const { _nextFirstDate, _nextSecondDate } = getFurtherOptions();
    dispatch(
      CALENDAR.setHiveDate({
        date: [_nextFirstDate.toJSDate(), _nextSecondDate.toJSDate()],
        timeSet: selectedTimeSet,
      } as any)
    );
  }

  function goToPrevDate() {
    const { _previousFirstDate, _previousSecondDate } = getPreviuosOptions();
    dispatch(
      CALENDAR.setHiveDate({
        date: [_previousFirstDate.toJSDate(), _previousSecondDate.toJSDate()],
        timeSet: selectedTimeSet,
      } as any)
    );
  }

  function toggleCalendar() {
    if (showCalendar === true) {
      dispatch(CALENDAR.setHiveCalendarDate(null as any));
      dispatch(CALENDAR.setHiveCalendarTimeSet(null as any));
    }

    setShowCalendar(!showCalendar);
  }

  return (
    <div className={styles.HiveCalendar__Input}
    style={
      ENV.VITE_APP_IS_WEB === 'true'
      ? {}
      : { backgroundColor: calendarColor || '#f7f7f7' }}
    >
      <div
        className={`
        ${styles.HiveCalendar__Input__Arrow}
        ${
          canGoBackward() === false
            ? styles.HiveCalendar__Input__Arrow__Disabled
            : ''
        }
        `}
        onClick={() => (canGoBackward() ? goToPrevDate() : null)}
        // FLEXO-6133 #6e: keyboard-operable + announced (WCAG 2.1.1).
        role="button"
        tabIndex={0}
        aria-label={t('calendar.a11y.previousPeriod')}
        aria-disabled={canGoBackward() === false}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && canGoBackward()) {
            e.preventDefault();
            goToPrevDate();
          }
        }}
      >
        <IconWrapper iconName="chevron-left" />
      </div>

      <div
        className={styles.HiveCalendar__Input__Content}
        onClick={() => toggleCalendar()}
        // FLEXO-6133 #6e: keyboard-operable + announced (WCAG 2.1.1). The
        // current period (text content) completes the accessible name.
        role="button"
        tabIndex={0}
        aria-label={t('calendar.a11y.openCalendar')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleCalendar();
          }
        }}
        style={
          ENV.VITE_APP_IS_WEB === 'true'
          ? {}
          : { backgroundColor: calendarColor || '#f7f7f7' }}
      >
        {getCalendarInputText()}
      </div>

      <div
        className={`
        ${styles.HiveCalendar__Input__Arrow}
        ${
          canGoFurther() === false
            ? styles.HiveCalendar__Input__Arrow__Disabled
            : ''
        }
        `}
        onClick={() => (canGoFurther() ? goToNextDate() : null)}
        // FLEXO-6133 #6e: keyboard-operable + announced (WCAG 2.1.1).
        role="button"
        tabIndex={0}
        aria-label={t('calendar.a11y.nextPeriod')}
        aria-disabled={canGoFurther() === false}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && canGoFurther()) {
            e.preventDefault();
            goToNextDate();
          }
        }}
      >
        <IconWrapper iconName="chevron-right" />
      </div>
    </div>
  );
}
